<template>
  <div
    :class="containerClass"
    aria-live="polite"
    role="region"
    aria-label="Notifications"
  >
    <TransitionGroup name="toast">
      <div
        v-for="toast in toastStore.toasts"
        :key="toast.id"
        class="pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border text-sm font-medium w-full sm:w-auto"
        :class="toastClasses(toast.type)"
        role="status"
      >
        <span class="material-icons text-lg shrink-0" aria-hidden="true">{{ iconFor(toast.type) }}</span>
        <p class="flex-1 min-w-0 font-bold">{{ toast.message }}</p>
        <a
          v-if="toast.action?.href"
          :href="toast.action.href"
          class="shrink-0 font-semibold underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-1 rounded px-2 py-1"
        >
          {{ toast.action.label }}
        </a>
        <button
          type="button"
          class="shrink-0 p-1 rounded opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1"
          title="Dismiss"
          aria-label="Dismiss"
          @click="toastStore.remove(toast.id)"
        >
          <span class="material-icons text-lg">close</span>
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useToastStore } from '@/stores/toast'
import { getSubdomainSlug } from '@/utils/subdomain'

const toastStore = useToastStore()
const route = useRoute()

/** Public restaurant page: either /r/:slug (PublicRestaurant) or subdomain (Landing shows PublicRestaurantView). */
const isPublicPage = computed(
  () => route.name === 'PublicRestaurant' || (route.name === 'Landing' && !!getSubdomainSlug()),
)

const containerClass = computed(() => {
  const base = 'fixed z-[99999] flex flex-col gap-2 pointer-events-none'
  const mobile = 'bottom-20 left-0 right-0 w-full p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]'
  if (isPublicPage.value) {
    return `${base} ${mobile} sm:bottom-4 sm:left-1/2 sm:right-auto sm:w-auto sm:max-w-sm sm:-translate-x-1/2 sm:p-0 sm:pb-[max(0.75rem,env(safe-area-inset-bottom))]`
  }
  return `${base} ${mobile} sm:bottom-auto sm:top-4 sm:right-4 sm:left-auto sm:w-auto sm:max-w-sm sm:p-0 sm:pb-0`
})

function toastClasses(type) {
  const base = 'border'
  const byType = {
    success: 'border-green-200 dark:border-green-800/50 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200',
    error: 'border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200',
    info: 'border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200',
  }
  return `${base} ${byType[type] ?? byType.info}`
}

function iconFor(type) {
  const icons = { success: 'check_circle', error: 'error', info: 'info' }
  return icons[type] ?? 'info'
}
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.2s ease;
}
.toast-enter-from {
  opacity: 0;
  transform: translateY(1rem);
}
.toast-leave-to {
  opacity: 0;
  transform: translateY(1rem);
}
@media (min-width: 640px) {
  .toast-enter-from,
  .toast-leave-to {
    transform: translateX(1rem);
  }
}
.toast-move {
  transition: transform 0.2s ease;
}
</style>
